/* eslint-disable boundaries/element-types */
import {
  Alert,
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import EditorComponent from 'Containers/Deal/EditDeal/components/EditorComponent';
import CheckboxField from 'components/CheckboxField';
import { MODEL_SERVICE_CALL } from 'constants/models';
import InputAutocompleteQuery from 'components/InputAutocompleteQuery';
import RichEditorControl from 'components/RichEditorControl';
import useForm from 'hooks/useForm';
import useNotification from 'hooks/useNotification';
import { COLOR_ERROR, COLOR_PRIMARY } from 'utils/constants/colors';
import dayjs from 'utils/dayjs';

import useAppointmentReminderSettings from '../hooks/useAppointmentReminderSettings';
import {
  FIELD_END,
  FIELD_ID,
  FIELD_MODEL,
  FIELD_MODEL_ID,
  FIELD_NOTES,
  FIELD_SYNC_WORK_ORDER_REASON,
  FIELD_NOTIFY_CUSTOMER,
  FIELD_NOTIFY_TECH,
  FIELD_SEPARATE_EVENT_PER_TECH,
  FIELD_START,
  FIELD_SYSTEM,
  FIELD_TECHNICIAN_USERS,
  FIELD_TITLE,
  FIELD_SMS_REMINDER_ENABLED,
  FIELD_REMINDERS,
} from '../constants/fields';
import { splitEventPerDayPerUser } from '../utils/splitEventPerDayPerUser';
import DateTimePickerField from './DateTimePickerField';
import WorkOrderReasonSyncField from './WorkOrderReasonSyncField';

export const INITIAL_VALUES = {
  [FIELD_MODEL]: null,
  [FIELD_MODEL_ID]: null,
  [FIELD_TITLE]: '',
  [FIELD_NOTES]: '',
  [FIELD_SYNC_WORK_ORDER_REASON]: false,
  [FIELD_TECHNICIAN_USERS]: [],
  [FIELD_SYSTEM]: null,
  [FIELD_START]: null,
  [FIELD_END]: null,
  [FIELD_NOTIFY_CUSTOMER]: true,
  [FIELD_NOTIFY_TECH]: true,
  [FIELD_SEPARATE_EVENT_PER_TECH]: false,
  [FIELD_SMS_REMINDER_ENABLED]: false,
  [FIELD_REMINDERS]: [],
};

function getEventCreationConfirmation(values) {
  if (
    values[FIELD_ID] ||
    !values[FIELD_START] ||
    !values[FIELD_END] ||
    !values[FIELD_TECHNICIAN_USERS]?.length
  ) {
    return null;
  }

  const eventsToCreateCount = splitEventPerDayPerUser(values).length;

  if (eventsToCreateCount <= 1) {
    return null;
  }

  const daysToCreateCount = splitEventPerDayPerUser({
    ...values,
    [FIELD_SEPARATE_EVENT_PER_TECH]: false,
  }).length;

  const techniciansToCreateCount = values[FIELD_SEPARATE_EVENT_PER_TECH]
    ? (values[FIELD_TECHNICIAN_USERS]?.length ?? 0)
    : 1;

  if (daysToCreateCount > 1 && techniciansToCreateCount > 1) {
    return {
      count: eventsToCreateCount,
      message: `Selected Start / End period is longer than one day. This will create ${eventsToCreateCount} events (one per day for each technician).`,
    };
  }

  if (daysToCreateCount > 1) {
    return {
      count: eventsToCreateCount,
      message: `Selected Start / End period is longer than one day. This will create ${eventsToCreateCount} events (one per day).`,
    };
  }

  return {
    count: eventsToCreateCount,
    message: `This will create ${eventsToCreateCount} events (one per technician).`,
  };
}

const SMS_REMINDERS_MAX_COUNT = 2;

const UNIT_TO_MINUTES = { minutes: 1, hours: 60, days: 1440 };

function minutesBeforeToItem(minutesBefore) {
  if (minutesBefore % 1440 === 0)
    return { value: minutesBefore / 1440, unit: 'days' };
  if (minutesBefore % 60 === 0)
    return { value: minutesBefore / 60, unit: 'hours' };
  return { value: minutesBefore, unit: 'minutes' };
}

function itemToReminderPayload(item) {
  return {
    minutes_before: Math.max(
      1,
      (parseInt(item.value, 10) || 1) * UNIT_TO_MINUTES[item.unit],
    ),
    message: item.message || null,
  };
}

const EventForm = ({
  submitting = false,
  cancelLabel = 'Cancel',
  submitLabel = 'Submit',
  submitLoadingLabel = 'Submitting…',
  errorMessage = null,
  values: initialValues = INITIAL_VALUES,
  errors: initialErrors = {},
  techniciansFieldOptions = [],
  systemFieldOptions = [],
  canSelectSystem = false,
  canNotifyCustomer = false,
  canNotifyTech = false,
  onSubmit,
  onCancel,
}) => {
  // Hooks

  const notification = useNotification();
  const { data: appointmentReminderSettings } =
    useAppointmentReminderSettings();
  const submitLockedRef = useRef(false);
  const autoInitSmsRef = useRef(false);
  const [submitLocked, setSubmitLocked] = useState(false);

  const [reminderItems, setReminderItems] = useState(() =>
    (initialValues[FIELD_REMINDERS] ?? []).map((r) => ({
      ...minutesBeforeToItem(r.minutes_before),
      message: r.message ?? '',
    })),
  );

  const handleSubmit = useCallback(
    async (values, ...args) => {
      if (submitting || submitLockedRef.current) {
        return;
      }

      submitLockedRef.current = true;
      setSubmitLocked(true);

      try {
        const eventCreationConfirmation = getEventCreationConfirmation(values);

        if (eventCreationConfirmation) {
          const confirmed = await notification.confirm({
            title: `Create ${eventCreationConfirmation.count} events?`,
            message: eventCreationConfirmation.message,
            confirmText: 'Create Events',
            cancelText: 'Cancel',
            icon: 'warning',
          });

          if (!confirmed) {
            return;
          }
        }

        await onSubmit(values, ...args);
      } finally {
        submitLockedRef.current = false;
        setSubmitLocked(false);
      }
    },
    [notification, onSubmit, submitting],
  );

  const form = useForm({
    initialValues,
    initialErrors,
    onSubmit: handleSubmit,
  });

  // Handlers

  const handleAddReminder = useCallback(() => {
    setReminderItems((prev) => {
      const next = [...prev, { value: 1, unit: 'hours', message: '' }];
      form.setFieldValue(FIELD_REMINDERS, next.map(itemToReminderPayload));
      return next;
    });
  }, [form]);

  const handleReminderChange = useCallback(
    (index, field, value) => {
      setReminderItems((prev) => {
        const next = prev.map((item, i) =>
          i === index ? { ...item, [field]: value } : item,
        );
        form.setFieldValue(FIELD_REMINDERS, next.map(itemToReminderPayload));
        return next;
      });
    },
    [form],
  );

  const handleRemoveReminder = useCallback(
    (index) => {
      setReminderItems((prev) => {
        const next = prev.filter((_, i) => i !== index);
        form.setFieldValue(FIELD_REMINDERS, next.map(itemToReminderPayload));
        return next;
      });
    },
    [form],
  );

  useEffect(() => {
    if (
      !autoInitSmsRef.current &&
      appointmentReminderSettings?.enabled &&
      !initialValues[FIELD_ID]
    ) {
      autoInitSmsRef.current = true;
      const hours = appointmentReminderSettings.hours ?? [];
      const defaults = hours.map((h) => ({
        value: h,
        unit: 'hours',
        message: '',
      }));
      setReminderItems(defaults);
      form.setFieldValue(FIELD_SMS_REMINDER_ENABLED, true);
      form.setFieldValue(FIELD_REMINDERS, defaults.map(itemToReminderPayload));
    }

    return () => {
      autoInitSmsRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentReminderSettings]);

  const handleSmsReminderEnabledChange = useCallback(
    (value) => {
      form.setFieldValue(FIELD_SMS_REMINDER_ENABLED, value);
      if (
        value &&
        reminderItems.length === 0 &&
        appointmentReminderSettings?.hours?.length
      ) {
        const defaults = appointmentReminderSettings.hours.map((h) => ({
          value: h,
          unit: 'hours',
          message: '',
        }));
        setReminderItems(defaults);
        form.setFieldValue(
          FIELD_REMINDERS,
          defaults.map(itemToReminderPayload),
        );
      }
    },
    [form, reminderItems.length, appointmentReminderSettings?.hours],
  );

  const handleCancelClick = useCallback(() => onCancel(), [onCancel]);

  // Computable

  const submitDisabled = useMemo(() => {
    if (submitting || submitLocked) {
      return true;
    }

    return (
      !form.values[FIELD_TITLE] ||
      !form.values[FIELD_TECHNICIAN_USERS]?.length ||
      !form.values[FIELD_START] ||
      !form.values[FIELD_END]
    );
  }, [form.values, submitLocked, submitting]);

  const inputsDisabled = useMemo(
    () => submitting || submitLocked,
    [submitLocked, submitting],
  );

  const systemFieldOptionsParams = useMemo(
    () => ({ deal_id: form.values[FIELD_MODEL_ID] }),
    [form.values],
  );

  const canCreateSeparateEventsPerTech = useMemo(
    () =>
      !initialValues[FIELD_ID] &&
      form.values[FIELD_TECHNICIAN_USERS]?.length > 1,
    [form.values, initialValues],
  );

  // Render

  return (
    <Box component="form" onSubmit={form.handleSubmit}>
      <Grid container={true} spacing={3}>
        {errorMessage && (
          <Grid item={true} md={12} xs={12}>
            <Alert severity={COLOR_ERROR}>{errorMessage}</Alert>
          </Grid>
        )}
        <Grid item={true} md={12} xs={12}>
          <TextField
            required={true}
            fullWidth={true}
            disabled={inputsDisabled}
            label="Title"
            variant="outlined"
            inputProps={{
              'data-testid': 'title_field',
            }}
            {...form.getFieldProps(FIELD_TITLE)}
            {...form.getMuiFieldErrorProps(FIELD_TITLE)}
          />
        </Grid>
        <Grid item={true} md={12} xs={12}>
          <RichEditorControl label="Notes" error={form.errors?.[FIELD_NOTES]}>
            <EditorComponent
              data-testid="notes_field"
              data={form.values[FIELD_NOTES] || ''}
              disableDebounce={true}
              disabled={inputsDisabled}
              handleInputChange={(data) => {
                form.setFieldValue(FIELD_NOTES, String(data));
              }}
            />
          </RichEditorControl>
        </Grid>
        {form.values[FIELD_MODEL] === MODEL_SERVICE_CALL && (
          <Grid item={true} md={12} xs={12}>
            <WorkOrderReasonSyncField
              disabled={inputsDisabled}
              value={Boolean(form.values[FIELD_SYNC_WORK_ORDER_REASON])}
              onChange={form.getFieldValueChangeHandler(FIELD_SYNC_WORK_ORDER_REASON)}
              error={Boolean(form.errors[FIELD_SYNC_WORK_ORDER_REASON])}
            />
          </Grid>
        )}
        {canSelectSystem && (
          <Grid item={true} md={12} xs={12}>
            <InputAutocompleteQuery
              disabled={inputsDisabled}
              queryKeyPrefix="event_form_systems"
              label="System"
              placeholder="Search System…"
              options={systemFieldOptions}
              optionsParams={systemFieldOptionsParams}
              value={form.values[FIELD_SYSTEM] ?? null}
              onChange={form.getFieldValueChangeHandler(FIELD_SYSTEM)}
              {...form.getMuiFieldErrorProps(FIELD_SYSTEM)}
              data-testid="system_field"
            />
          </Grid>
        )}
        <Grid item={true} md={12} xs={12}>
          <InputAutocompleteQuery
            disabled={inputsDisabled}
            required={true}
            multiple={true}
            queryKeyPrefix="event_form_technicians"
            label="Technicians"
            placeholder="Search Technicians…"
            options={techniciansFieldOptions}
            value={form.values[FIELD_TECHNICIAN_USERS] ?? []}
            onChange={form.getFieldValueChangeHandler(FIELD_TECHNICIAN_USERS)}
            {...form.getMuiFieldErrorProps(FIELD_TECHNICIAN_USERS)}
            data-testid="technicians_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <DateTimePickerField
            disabled={inputsDisabled}
            fieldErrorProps={form.getMuiFieldErrorProps(FIELD_START)}
            value={
              form.values[FIELD_START]
                ? dayjs(form.values[FIELD_START]).utc()
                : null
            }
            inputProps={{
              'data-testid': 'start_field',
            }}
            label="Start date"
            onChange={form.getFieldValueChangeHandler(
              FIELD_START,
              (value) => value?.utc().format() ?? null,
            )}
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <DateTimePickerField
            disabled={inputsDisabled}
            fieldErrorProps={form.getMuiFieldErrorProps(FIELD_END)}
            value={
              form.values[FIELD_END]
                ? dayjs(form.values[FIELD_END]).utc()
                : null
            }
            inputProps={{
              'data-testid': 'end_field',
            }}
            label="End date"
            onChange={form.getFieldValueChangeHandler(
              FIELD_END,
              (value) => value?.utc().format() ?? null,
            )}
          />
        </Grid>
        {canNotifyCustomer && (
          <Grid item={true} md={6} xs={12}>
            <CheckboxField
              disabled={inputsDisabled}
              label="Notify Customer"
              value={form.values[FIELD_NOTIFY_CUSTOMER] ?? false}
              onChange={form.getFieldValueChangeHandler(FIELD_NOTIFY_CUSTOMER)}
              {...form.getMuiFieldErrorProps(FIELD_NOTIFY_CUSTOMER)}
              data-testid="notify_customer_field"
            />
          </Grid>
        )}
        {canNotifyTech && (
          <Grid item={true} md={6} xs={12}>
            <CheckboxField
              disabled={inputsDisabled}
              label="Notify Tech"
              value={form.values[FIELD_NOTIFY_TECH] ?? false}
              onChange={form.getFieldValueChangeHandler(FIELD_NOTIFY_TECH)}
              {...form.getMuiFieldErrorProps(FIELD_NOTIFY_TECH)}
              data-testid="notify_tech_field"
            />
          </Grid>
        )}
        {canCreateSeparateEventsPerTech && (
          <Grid item={true} md={12} xs={12}>
            <CheckboxField
              disabled={inputsDisabled}
              label="Create separate event for each technician"
              value={form.values[FIELD_SEPARATE_EVENT_PER_TECH] ?? false}
              onChange={form.getFieldValueChangeHandler(
                FIELD_SEPARATE_EVENT_PER_TECH,
              )}
              {...form.getMuiFieldErrorProps(FIELD_SEPARATE_EVENT_PER_TECH)}
              data-testid="separate_per_tech"
            />
          </Grid>
        )}
        {appointmentReminderSettings?.enabled && (
          <Grid item={true} md={12} xs={12}>
            <CheckboxField
              disabled={inputsDisabled}
              label="SMS Reminders"
              value={form.values[FIELD_SMS_REMINDER_ENABLED] ?? false}
              onChange={handleSmsReminderEnabledChange}
              {...form.getMuiFieldErrorProps(FIELD_SMS_REMINDER_ENABLED)}
            />
          </Grid>
        )}
        {appointmentReminderSettings?.enabled &&
          form.values[FIELD_SMS_REMINDER_ENABLED] && (
            <Grid item={true} md={12} xs={12}>
              <Grid container={true} spacing={2}>
                {reminderItems.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid item={true} xs={12} key={index}>
                    <Grid container={true} spacing={1} alignItems="center">
                      <Grid item={true} xs={2}>
                        <TextField
                          type="number"
                          fullWidth={true}
                          disabled={inputsDisabled}
                          label="Time"
                          variant="outlined"
                          value={item.value}
                          onChange={(e) =>
                            handleReminderChange(index, 'value', e.target.value)
                          }
                          inputProps={{
                            min: 1,
                            'data-testid': `sms_reminder_${index}_value`,
                          }}
                        />
                      </Grid>
                      <Grid item={true} xs={3}>
                        <FormControl fullWidth={true} disabled={inputsDisabled}>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            label="Unit"
                            value={item.unit}
                            onChange={(e) =>
                              handleReminderChange(
                                index,
                                'unit',
                                e.target.value,
                              )
                            }
                            inputProps={{
                              'data-testid': `sms_reminder_${index}_unit`,
                            }}
                          >
                            <MenuItem value="minutes">Minutes</MenuItem>
                            <MenuItem value="hours">Hours</MenuItem>
                            <MenuItem value="days">Days</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item={true} xs={5}>
                        <TextField
                          fullWidth={true}
                          disabled={inputsDisabled}
                          label="Message (optional)"
                          variant="outlined"
                          value={item.message}
                          onChange={(e) =>
                            handleReminderChange(
                              index,
                              'message',
                              e.target.value,
                            )
                          }
                          inputProps={{
                            'data-testid': `sms_reminder_${index}_message`,
                          }}
                        />
                      </Grid>
                      <Grid item={true} xs={2}>
                        <Button
                          disabled={inputsDisabled}
                          type="button"
                          variant="text"
                          color={COLOR_ERROR}
                          size="small"
                          onClick={() => handleRemoveReminder(index)}
                          data-testid={`sms_reminder_${index}_remove`}
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
                <Grid item={true} xs={12}>
                  <Button
                    disabled={
                      inputsDisabled ||
                      reminderItems.length >= SMS_REMINDERS_MAX_COUNT
                    }
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={handleAddReminder}
                    data-testid="sms_reminder_add"
                  >
                    + Add Reminder
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        <Grid
          item={true}
          md={12}
          xs={12}
          display="flex"
          gap={2}
          justifyContent="space-between"
        >
          <Button
            disabled={inputsDisabled}
            type="button"
            variant="outlined"
            color={COLOR_PRIMARY}
            onClick={handleCancelClick}
            data-testid="cancel_button"
          >
            {cancelLabel}
          </Button>
          <Button
            disabled={submitDisabled}
            type="submit"
            variant="contained"
            color={COLOR_PRIMARY}
            data-testid="submit_button"
          >
            {submitting ? submitLoadingLabel : submitLabel}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

EventForm.propTypes = {
  submitting: PropTypes.bool,
  cancelLabel: PropTypes.string,
  submitLabel: PropTypes.string,
  submitLoadingLabel: PropTypes.string,
  errorMessage: PropTypes.string,
  values: PropTypes.shape({
    [FIELD_TITLE]: PropTypes.string,
    [FIELD_NOTES]: PropTypes.string,
    [FIELD_SYNC_WORK_ORDER_REASON]: PropTypes.bool,
    [FIELD_TECHNICIAN_USERS]: PropTypes.arrayOf(PropTypes.number),
    [FIELD_SYSTEM]: PropTypes.number,
    [FIELD_START]: PropTypes.string,
    [FIELD_END]: PropTypes.string,
    [FIELD_NOTIFY_CUSTOMER]: PropTypes.bool,
    [FIELD_NOTIFY_TECH]: PropTypes.bool,
    [FIELD_SMS_REMINDER_ENABLED]: PropTypes.bool,
    [FIELD_REMINDERS]: PropTypes.arrayOf(
      PropTypes.shape({
        minutes_before: PropTypes.number,
        message: PropTypes.string,
      }),
    ),
  }),
  techniciansFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      }),
    ),
  ]),
  systemFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      }),
    ),
  ]),
  errors: PropTypes.shape({
    [FIELD_TITLE]: PropTypes.string,
    [FIELD_NOTES]: PropTypes.string,
    [FIELD_SYNC_WORK_ORDER_REASON]: PropTypes.string,
    [FIELD_TECHNICIAN_USERS]: PropTypes.string,
    [FIELD_SYSTEM]: PropTypes.string,
    [FIELD_START]: PropTypes.string,
    [FIELD_END]: PropTypes.string,
    [FIELD_NOTIFY_CUSTOMER]: PropTypes.string,
    [FIELD_NOTIFY_TECH]: PropTypes.string,
    [FIELD_SMS_REMINDER_ENABLED]: PropTypes.string,
    [FIELD_REMINDERS]: PropTypes.string,
  }),
  canSelectSystem: PropTypes.bool,
  canNotifyCustomer: PropTypes.bool,
  canNotifyTech: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * @type {EventForm}
 */
export default memo(EventForm);
